import { computed } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/store/modules/user";
import { useTeamStore } from "@/store/modules/UserTeam";
import { routes } from "./routes";

function waitForTeamReady(
  teamStore: ReturnType<typeof useTeamStore>,
  timeout = 8000
): Promise<void> {
  if (!teamStore.teamLoading) return Promise.resolve();

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      console.warn("[Router] 等待团队数据初始化超时，将跳过权限检查");
      resolve();
    }, timeout);

    const stop = watch(() => teamStore.teamLoading, (loading) => {
      if (!loading) {
        clearTimeout(timer);
        stop();
        resolve();
      }
    });
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to, _from, next) => {
  if (to.meta.title) {
    document.title = `${to.meta.title} - JoinSpark`;
  }
  const userStore = useUserStore();
  const teamStore = useTeamStore();

  /**
   * 检查路由是否需要认证
   * @description 如果路由需要认证，且用户未登录，重定向到登录页
   */
  if (to.meta.requiresAuth) {
    if (!userStore.isLogin) {
      next("/login");
      return;
    }
  }

  /**
   * 等待团队数据初始化完成
   * isTeamAccount 依赖 currentTeam（通过异步 API 加载），
   * 必须等待 teamLoading 变为 false 后才能拿到准确的值
   */
  if (userStore.userInfo?.last_active_team_id) {
    await waitForTeamReady(teamStore);
  }

  /**
   * 当前已拥有的页面权限
   */
  const perms = computed(() => teamStore.currentTeam?.perms || []);

  // console.log(to.path, teamStore.isTeamAccount, teamStore.hasTeamPermission);
  // console.log(teamStore.currentTeam);

  /**
   * 需要检查路由是否需要权限
   * @description 如果路由需要权限，且用户未拥有该权限，重定向到无权限页
   */
  // 团队账号下
  if (teamStore.isTeamAccount) {
    // 有团队管理的权限，意味着用户是团队管理员，无需检查权限，随意访问
    if (teamStore.hasTeamPermission) {
      // 添加访问团队相关路由的判断逻辑，保证跳转到团队首页
      if (to.path === "/team" || to.path === "/team/notification") {
        next("/team/index");
        return;
      } else if (to.path === "/no-permission") {
        next("/app/dashboard");
        return;
      }
      next();
    } else {
      // 非团队管理员，在团队账号下，需要检查路由是否需要权限
      if (
        to.path.indexOf("/team") > -1 ||
        (to.meta.requirePerm && !perms.value.includes(to.meta.permission as string))
      ) {
        next("/no-permission");
      } else {
        next();
      }
    }
  } else {
    if (to.path === "/team" || to.path === "/team/index") {
      next("/team/notification");
      return;
    }
    if (to.path === "/no-permission") {
      next("/app/dashboard");
      return;
    }
    next();
  }
});

export default router;
